import { createContext, useContext, useMemo, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { useMyData } from './useCampaigns'
import { supabase } from '../lib/supabase'

type CampaignRole = 'gm' | 'player' | 'spectator'

interface CampaignRoleContextValue {
  role: CampaignRole
  isGM: boolean
  isPlayer: boolean
  userId: string | null
}

const CampaignRoleContext = createContext<CampaignRoleContextValue | null>(null)

interface ProviderProps {
  campaignId: string | undefined
  children: ReactNode
}

export function CampaignRoleProvider({ campaignId, children }: ProviderProps) {
  const { data: memberships } = useMyData()
  const [userId, setUserId] = useState<string | null>(null)

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null))
  }, [])

  const value = useMemo<CampaignRoleContextValue>(() => {
    if (!memberships || !campaignId) {
      return { role: 'player', isGM: false, isPlayer: true, userId }
    }
    const membership = memberships.find(m => {
      const campaign = m.campaigns as unknown as { id: string } | null
      return campaign?.id === campaignId
    })
    const role: CampaignRole = (membership?.role as CampaignRole) ?? 'player'
    return { role, isGM: role === 'gm', isPlayer: role === 'player', userId }
  }, [memberships, campaignId, userId])

  return (
    <CampaignRoleContext.Provider value={value}>
      {children}
    </CampaignRoleContext.Provider>
  )
}

export function useCampaignRole(): CampaignRoleContextValue {
  const ctx = useContext(CampaignRoleContext)
  if (!ctx) throw new Error('useCampaignRole must be used within a CampaignRoleProvider')
  return ctx
}
